/**
 * Zeichensatz, Format und Normalisierung von Codes und Teilnehmer-Tokens (§7.1 bis §7.4).
 *
 * Liegt im gemeinsamen Paket, damit die Anmeldemaske clientseitig genau dieselbe Prüfung anwendet
 * wie der Server. Die clientseitige Prüfung dient dabei ausschließlich der Benutzerführung und
 * ersetzt niemals die serverseitige (§13.1, §25.2).
 *
 * Enthält ausschließlich Konstanten und reine Funktionen — keine Erzeugung, kein Schlüsselzugriff
 * (§41.2). Die kryptografisch zufällige Erzeugung liegt im Backend.
 */
/**
 * Zulässige Zeichen (§7.1).
 *
 * Buchstaben A bis Z **ohne** `I`, `L` und `O` sowie Ziffern 2 bis 9. `0` und `1` entfallen
 * ebenfalls. Grund ist die Verwechslungsgefahr beim Abtippen von einer gedruckten Karte:
 * `I`/`l`/`1` und `O`/`0` sind in vielen Schriften kaum zu unterscheiden.
 *
 * Ergibt 23 Buchstaben und 8 Ziffern, also **31 Zeichen**.
 */
export const CODE_ZEICHENSATZ = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
/** Länge des Veranstaltungscodes (§7.2). */
export const VERANSTALTUNGSCODE_LAENGE = 4;
/** Länge des Berechtigungscodes (§7.3). */
export const BERECHTIGUNGSCODE_LAENGE = 6;
/** Länge des vollständigen Teilnehmer-Tokens in interner Normalform (§7.4). */
export const TOKEN_LAENGE = VERANSTALTUNGSCODE_LAENGE + BERECHTIGUNGSCODE_LAENGE;
/**
 * Höchstzahl je Veranstaltung jemals erzeugter Tokens (§7.5).
 *
 * Gezählt werden gültige, gesperrte und ersetzte Tokens gemeinsam.
 */
export const MAX_TOKENS_JE_VERANSTALTUNG = 1000;
/** Höchstzahl gleichzeitig angemeldeter Teilnehmer (§7.5, §39.1). */
export const MAX_GLEICHZEITIGE_TEILNEHMER = 250;
const ZEICHENSATZ_MENGE = new Set(CODE_ZEICHENSATZ);
/**
 * Bringt eine Eingabe in die interne Normalform (§7.1, §7.4).
 *
 * Eingaben sind nicht groß-/kleinschreibungssensitiv; Leerzeichen und Bindestriche werden
 * ignoriert. Aus `ab7k-p3x9r2` wird `AB7KP3X9R2`.
 */
export function normalisiereToken(eingabe) {
    return eingabe.replace(/[\s-]/g, '').toUpperCase();
}
/** Ob eine Zeichenkette ausschließlich aus zulässigen Zeichen besteht (§7.1). */
export function istGueltigerCode(normalisiert, laenge) {
    if (normalisiert.length !== laenge) {
        return false;
    }
    for (const zeichen of normalisiert) {
        if (!ZEICHENSATZ_MENGE.has(zeichen)) {
            return false;
        }
    }
    return true;
}
/**
 * Ob eine Eingabe in Normalform ein formal gültiger Teilnehmer-Token ist (§7.4).
 *
 * Prüft ausschließlich die Form. Ob der Token existiert, gültig ist oder gesperrt wurde, darf
 * nach §9.2 nach außen nicht unterscheidbar sein und wird ausschließlich serverseitig geprüft.
 */
export function istGueltigesTokenFormat(normalisiert) {
    return istGueltigerCode(normalisiert, TOKEN_LAENGE);
}
/**
 * Stellt einen Token lesbar dar: `AB7K-P3X9R2` (§7.4).
 *
 * Der Bindestrich trennt Veranstaltungs- von Berechtigungscode und erleichtert das Abtippen.
 *
 * @returns die Eingabe unverändert, wenn sie nicht die erwartete Länge hat — die Darstellung soll
 *   nichts erfinden.
 */
export function formatiereToken(normalisiert) {
    if (normalisiert.length !== TOKEN_LAENGE) {
        return normalisiert;
    }
    return `${normalisiert.slice(0, VERANSTALTUNGSCODE_LAENGE)}-${normalisiert.slice(VERANSTALTUNGSCODE_LAENGE)}`;
}
/** Zerlegt einen Token in seine beiden Bestandteile (§7.4). */
export function zerlegeToken(normalisiert) {
    if (!istGueltigesTokenFormat(normalisiert)) {
        return undefined;
    }
    return {
        veranstaltungscode: normalisiert.slice(0, VERANSTALTUNGSCODE_LAENGE),
        berechtigungscode: normalisiert.slice(VERANSTALTUNGSCODE_LAENGE),
    };
}
//# sourceMappingURL=codes.js.map